// ABOUTME: Thin fetch wrappers for the media endpoints the MEDIA UI drives.
// ABOUTME: POST /api/media stays for legacy file/image fields; node-backed media goes through /api/media-nodes.

export type MediaKind = 'folder' | 'image' | 'document' | 'video' | 'link';

/** What the upload observed server-side and relays back, to be stored on the node. */
export type UploadedMedia = {
    mediaId: string;
    fileName: string;
    mimeType: string;
    extension: string;
};

export type CreateMediaNodeBody = {
    mediaId?: string;
    fileName?: string;
    mimeType?: string;
    extension?: string;
    url?: string;
    mediaType?: MediaKind;
    code?: string;
    parentNodeId?: string;
    displayName?: string;
    pathKey?: string;
    importDestinationNodeId?: string;
};

export type CreatedMediaNode = {
    nodeId: string;
    code: string;
    hierarchyId: string;
    mediaType: MediaKind;
};

/** A URL the server refused to download, with the stable `reason` code the URL dialog maps. */
export class MediaUrlRejectedError extends Error {
    constructor(public readonly reason: string) {
        super(reason);
        this.name = 'MediaUrlRejectedError';
    }
}

const readErrorBody = (response: Response): Promise<Record<string, unknown>> =>
    response.json().catch(() => ({}));

/**
 * The server's own message, or an empty one: the caller shows its localized fallback, so no
 * untranslated text is ever surfaced from here.
 */
const serverError = (body: Record<string, unknown>) =>
    new Error(
        [body.message, body.error].find((value): value is string => typeof value === 'string') ??
            '',
    );

const asError = async (response: Response) => serverError(await readErrorBody(response));

/** A refused URL carries a `reason` code the URL dialog maps; anything else is a plain error. */
const asUrlError = async (response: Response) => {
    const body = await readErrorBody(response);
    return typeof body.reason === 'string'
        ? new MediaUrlRejectedError(body.reason)
        : serverError(body);
};

/**
 * Stores the bytes and returns what the server observed about each file, in the same order.
 *
 * Deliberately not `POST /api/media`: that endpoint also writes a `Media` projection row, which
 * only the legacy `file`/`image` fields need. A MEDIA node carries its own metadata, so this path
 * persists nothing outside the bucket.
 */
export const uploadMediaFiles = async (files: File[]): Promise<UploadedMedia[]> => {
    const form = new FormData();
    files.forEach(file => form.append('files', file));

    const response = await fetch('/api/media-nodes/upload', {
        method: 'POST',
        credentials: 'include',
        body: form,
    });
    if (!response.ok) {
        throw await asError(response);
    }
    return response.json();
};

export const createMediaNode = async (body: CreateMediaNodeBody): Promise<CreatedMediaNode> => {
    const response = await fetch('/api/media-nodes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw await asError(response);
    }
    return response.json();
};

/**
 * Pre-flight of a directory import: maps the `pathKeys` that already have a node to their id, so
 * the client uploads only the missing ones.
 */
export const resolveExistingMediaNodes = async (
    parentNodeId: string,
    pathKeys: string[],
): Promise<Record<string, string>> => {
    const response = await fetch('/api/media-nodes/resolve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ parentNodeId, pathKeys }),
    });
    if (!response.ok) {
        throw await asError(response);
    }
    const body: { existing?: Record<string, string> } = await response.json();
    return body.existing ?? {};
};

/** What a replacement wrote on the node: the asset id is the one it already had. */
export type ReplacedMediaAsset = {
    nodeId: string;
    assetId: string;
    fileName: string;
    mimeType: string;
    extension: string;
    mediaType: MediaKind;
};

const assetUrlOf = (nodeId: string) => `/api/media-nodes/${encodeURIComponent(nodeId)}/asset`;

/**
 * Puts `file` behind the asset of a MEDIA node, under the asset id it already has: every field
 * referencing the node keeps pointing at it. Saved immediately — the node is updated server-side.
 */
export const replaceMediaAsset = async (
    nodeId: string,
    file: File,
): Promise<ReplacedMediaAsset> => {
    const form = new FormData();
    form.append('files', file);

    const response = await fetch(assetUrlOf(nodeId), {
        method: 'PUT',
        credentials: 'include',
        body: form,
    });
    if (!response.ok) {
        throw await asError(response);
    }
    return response.json();
};

/** Same, with the file downloaded from `url` by the server. */
export const replaceMediaAssetFromUrl = async (
    nodeId: string,
    url: string,
): Promise<ReplacedMediaAsset> => {
    const response = await fetch(assetUrlOf(nodeId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url }),
    });
    if (!response.ok) {
        throw await asUrlError(response);
    }
    return response.json();
};

/**
 * Downloads one image from `url` and describes it back, like `uploadMediaFiles` for a dropped file:
 * stored in the bucket only, with the metadata the node is created with. A refused URL throws
 * `MediaUrlRejectedError`, with the reason the URL dialog maps.
 */
export const downloadMediaFromUrl = async (url: string): Promise<UploadedMedia> => {
    const response = await fetch('/api/media-nodes/download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url }),
    });
    if (!response.ok) {
        throw await asUrlError(response);
    }
    return response.json();
};
